import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
// components
import Notification from '../../atoms/notification';
import Header from '../../organisms/header';
import Footer from '../../organisms/footer';
// types
import type { decodedTokenInfoType, footerCancelButtonVariantType, stateType, userType } from '../../../types/types';
// styles
import './index.scss';
import HeaderDark from '../../organisms/header/headerDark';
import { useTheme } from '../../../hooks/theme';
import { usePatientZoomWarningNotification } from '../../../hooks/usePatientZoomWarningNotification';

interface AppTemplateProps {
  children: React.ReactNode;
  heading: string;
  description: string;
  isHeaderHidden?: boolean;
  handleSubmit?: () => void;
  handleCancel?: () => void;
  renderDescription?: () => JSX.Element | undefined;
  submitHidden?: boolean;
  cancelHidden?: boolean;
  disabled?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  isCancelDecline?: boolean;
  path?: string;
  isConnectStatusShown?: boolean;
  height?: boolean;
  isUserConnected?: boolean;
  customUserType?: userType;
  connectionLabelHidden?: boolean;
  className?: string;
  decodedData?: decodedTokenInfoType;
  isIntegration?: boolean;
  isDevice?: boolean;
  cancelButtonVariant?: footerCancelButtonVariantType;
}

const AppTemplate: FC<AppTemplateProps> = ({
  children,
  handleSubmit,
  handleCancel,
  submitHidden,
  cancelHidden,
  disabled,
  heading,
  description,
  isHeaderHidden,
  submitLabel,
  cancelLabel,
  isCancelDecline,
  path,
  height,
  isConnectStatusShown,
  isUserConnected,
  customUserType,
  connectionLabelHidden,
  className,
  decodedData,
  isDevice,
  isIntegration,
  renderDescription,
  cancelButtonVariant,
}: AppTemplateProps) => {
  const { isDark } = useTheme();
  const locationPath = window.location.pathname;

  usePatientZoomWarningNotification(locationPath);

  //store
  const userType = useSelector((state: stateType) => state.user.user.userType);

  const HeaderToRender = isDark ? HeaderDark : Header;

  const isNavigationHidden =
    userType !== 'patient' || path === '/result/:id' || locationPath.includes('/self_test') || locationPath === '/test';

  const isPatientNonTestFlow = userType === 'patient' && locationPath !== '/test';

  // iOS zoom disabling
  useEffect(() => {
    const pinchZoom = (e: TouchEvent) => {
      if (e.touches.length > 1 && e.cancelable) {
        e.preventDefault();
      }
    };

    let lastTouchEnd = 0;

    const doubleTapZoom = (e: TouchEvent) => {
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300 && e.cancelable) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener('touchmove', pinchZoom, { passive: false });
    document.addEventListener('touchend', doubleTapZoom, { passive: false });

    return () => {
      document.removeEventListener('touchmove', pinchZoom);
      document.removeEventListener('touchend', doubleTapZoom);
    };
  }, []);

  return (
    <>
      <Notification />
      <div className={`content${className ? className : ''}${isPatientNonTestFlow ? ' content--patient-flow' : ''}`}>
        {!isHeaderHidden && (
          <HeaderToRender
            userType={customUserType}
            path={locationPath}
            navigationHidden={isNavigationHidden}
            isIntegration={!!isIntegration}
            isDevice={!!isDevice}
            isUserConnected={isUserConnected}
            decodedData={decodedData}
          />
        )}
        <div className="content__inner">
          {(heading || description) && (
            <div className="content__heading-container">
              {heading && <h3 className="page-heading">{heading}</h3>}
              {renderDescription ? (
                renderDescription()
              ) : (
                <div tabIndex={description.length < 1 ? -1 : 0}>
                  <span className="page-description">{description}</span>
                </div>
              )}
            </div>
          )}
          {children}
        </div>
        <Footer
          handleSubmit={handleSubmit}
          submitHidden={submitHidden}
          cancelHidden={cancelHidden}
          handleCancel={handleCancel}
          disabled={disabled}
          height={height}
          isDevice={isDevice}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
          isCancelDecline={isCancelDecline}
          isConnectStatusShown={isConnectStatusShown}
          isUserConnected={isUserConnected}
          userType={customUserType}
          connectionLabelHidden={connectionLabelHidden}
          cancelButtonVariant={cancelButtonVariant}
        />
      </div>
    </>
  );
};

export default AppTemplate;
